import type { IGraphics } from '../../rendering/IRenderer';
import { seededRand } from '../../effects/atmosphere/atmospherePlacement';
import type { PlatformAccentBounds } from '../organicPlatformAccent';

export type TownMaterialGraphics = Pick<IGraphics, 'fillRect' | 'fillPolygon' | 'fillVerticalGradientRect'>;
export interface TownQuayPalette {
  readonly face: number; readonly deep: number; readonly mortar: number;
  readonly stones: readonly number[];
  readonly cap: number; readonly edge: number; readonly wet: number;
}
export interface TownTimberPalette {
  readonly face: number; readonly shade: number; readonly grain: number;
  readonly cap: number; readonly edge: number; readonly iron: number;
}

export function validTownSurface(p: PlatformAccentBounds): boolean {
  return [p.x, p.y, p.width, p.height, p.x + p.width, p.y + p.height].every(Number.isFinite)
    && p.width > 0 && p.height > 0;
}

const MAX_STONE_COLUMNS = 16;
const MAX_STONE_ROWS = 4;
const MAX_PLANKS = 16;

/** Bounded static masonry. Palette and cap choices belong to the caller. */
export function drawTownQuay(g: TownMaterialGraphics, p: PlatformAccentBounds, seed: number, QUAY: TownQuayPalette, cap?: Readonly<{ face: number; edge: number }>): void {
  if (!validTownSurface(p) || QUAY.stones.length === 0) return;
  const { x, y, width, height } = p;
  if (g.fillVerticalGradientRect) {
    g.fillVerticalGradientRect(x, y, width, height, QUAY.face, QUAY.deep, 1);
  } else {
    g.fillRect(x, y, width, height, QUAY.face, 1);
  }
  const capHeight = Math.min(14, height);
  const courseHeight = 45;
  const rows = Math.min(MAX_STONE_ROWS, Math.ceil((height - capHeight) / courseHeight));
  const columns = Math.min(MAX_STONE_COLUMNS, Math.max(1, Math.ceil(width / 112)));
  const stoneWidth = width / columns;
  for (let row = 0; row < rows; row++) {
    const top = y + capHeight + row * courseHeight;
    const bottom = Math.min(y + height, top + courseHeight);
    if (bottom - top < 3) continue;
    // Alternating half blocks, clipped to the authored quay's ends.
    for (let col = 0; col <= columns; col++) {
      const left = Math.max(x, x + (col - (row % 2) * 0.5) * stoneWidth);
      const right = Math.min(x + width, x + (col + 1 - (row % 2) * 0.5) * stoneWidth);
      if (right - left < 4) continue;
      const variation = seededRand(seed + row * 31 + col * 7);
      const color = QUAY.stones[Math.floor(variation * QUAY.stones.length)];
      const alpha = 0.78 - row * 0.13;
      g.fillRect(left + 1, top + 1, right - left - 2, bottom - top - 2, color, alpha);
      g.fillRect(left + 3, top + 2, Math.max(0, right - left - 6), 1, QUAY.cap, alpha * 0.4);
      if (variation > 0.72 && right - left > 24 && bottom - top > 22) {
        g.fillPolygon([
          { x: left + 12, y: top + 3 }, { x: left + 15, y: top + 13 },
          { x: left + 13, y: top + 20 }, { x: left + 14, y: top + 12 },
        ], QUAY.mortar, alpha * 0.7);
      }
    }
  }
  // A continuous, level rim makes the actual collision height readable.
  g.fillRect(x, y, width, capHeight, cap?.face ?? QUAY.cap, 1);
  g.fillRect(x, y, width, Math.min(2, height), cap?.edge ?? QUAY.edge, 0.9);
  for (let col = 1; col < columns && capHeight > 2; col++) {
    g.fillRect(x + col * stoneWidth, y + 2, Math.min(2, stoneWidth), capHeight - 2, QUAY.mortar, 0.62);
  }
  if (height > 200) {
    g.fillRect(x, y + 196, width, 4, QUAY.wet, 0.22);
  }
}

/** Timber cap and optional supports; no scene or platform identities enter the builder. */
export function drawTownTimber(g: TownMaterialGraphics, p: PlatformAccentBounds, groundY: number, seed: number, TIMBER: TownTimberPalette, support: 'full' | 'braces' | 'none'): void {
  if (!validTownSurface(p)) return;
  const { x, y, width, height } = p;
  const bottom = y + height;
  const availableDrop = Number.isFinite(groundY) ? Math.max(0, groundY - bottom) : 0;
  const fullSupport = support === 'full';
  const drop = support === 'none' ? 0 : Math.min(availableDrop, fullSupport ? 340 : Math.min(42, width * 0.22));
  const postWidth = Math.min(12, width * 0.06);
  const inset = Math.min(28, width * 0.15);
  for (const postX of [x + inset, x + width - inset - postWidth]) {
    if (drop <= 0) break;
    if (fullSupport) {
      g.fillRect(postX, bottom, postWidth, drop, TIMBER.shade, 0.94);
      const grainWidth = Math.min(2, postWidth * 0.2);
      g.fillRect(postX + grainWidth, bottom, grainWidth, drop, TIMBER.grain, 0.52);
      g.fillRect(postX, bottom + drop - Math.min(10, drop), postWidth, Math.min(10, drop), TIMBER.iron, 1);
    }
    const towardsCenter = postX < x + width / 2 ? 1 : -1;
    const reach = Math.min(40, width * 0.22);
    g.fillPolygon([
      { x: postX, y: bottom },
      { x: postX + towardsCenter * reach, y: bottom },
      { x: postX + towardsCenter * reach, y: bottom + Math.min(5, drop) },
      { x: postX + postWidth, y: bottom + Math.min(drop, 46) },
      { x: postX, y: bottom + Math.min(drop, 46) },
    ], TIMBER.face, 0.94);
  }
  g.fillRect(x, y, width, height, TIMBER.face, 1);
  g.fillRect(x, bottom - Math.min(5, height), width, Math.min(5, height), TIMBER.shade, 1);
  const capHeight = Math.min(7, height);
  g.fillRect(x, y, width, capHeight, TIMBER.cap, 1);
  g.fillRect(x, y, width, Math.min(2, height), TIMBER.edge, 0.92);
  const planks = Math.min(MAX_PLANKS, Math.max(1, Math.ceil(width / 46)));
  const plankWidth = width / planks;
  for (let plank = 0; plank < planks; plank++) {
    const left = x + plank * plankWidth;
    const grainY = y + Math.min(height * 0.6, capHeight + 4 + seededRand(seed + plank) * 5);
    if (plank > 0 && height > 4) g.fillRect(left, y + 2, Math.min(1, plankWidth), height - 4, TIMBER.shade, 0.66);
    g.fillRect(left + plankWidth * 0.16, grainY, plankWidth * 0.6, Math.min(1, height * 0.4), TIMBER.grain, 0.58);
    if (height >= 12 && plankWidth >= 12) {
      g.fillRect(left + 4, y + capHeight + 2, 2, 2, TIMBER.iron, 0.9);
    }
  }
}
